import React, {useEffect, useMemo, useState} from 'react';
import PlantFinderPlantResultCard from "./PlantFinderPlantResultCard";
import '../../css/plantFinderStylings/PlantFinderPlantResultsStyling.css';
import {supabase} from "../lib/supabaseClient";

const PlantFinderPlantResults = ({ filters = {} }) => {
    const {
        searchTerm = '',
        plantType = '',
        growingSeason = '',
        lifecycle = '',
        soilPreference = '',
        sunlightLevels = '',
        plantSize = '',
        storageLife = '',
        hardinessZone = '',
        waterNeeds = '',
        checkboxStates = {}
    } = filters;

    const [allPlants, setAllPlants] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchVeggies = async () => {
            console.log("Fetching plant data");
            const { data, error } = await supabase
                .from('plant')
                .select('*')
                .order('plant_name', { ascending: true });

            if (error) {
                console.error('Error fetching plants:', error);
            } else {
                setAllPlants(data);
                console.log(data);
            }

            setLoading(false);
        };

        fetchVeggies();
    }, []);

    // Check if any filters are active
    const hasActiveFilters = useMemo(() => {
        try {
            return (
                searchTerm ||
                plantType ||
                growingSeason ||
                lifecycle ||
                soilPreference ||
                sunlightLevels ||
                plantSize ||
                storageLife ||
                hardinessZone ||
                waterNeeds ||
                (checkboxStates && typeof checkboxStates === 'object' &&
                    Object.values(checkboxStates).some(Boolean))
            );
        } catch (error) {
            console.error('Error checking active filters:', error);
            return false;
        }
    }, [
        searchTerm,
        plantType,
        growingSeason,
        lifecycle,
        soilPreference,
        sunlightLevels,
        plantSize,
        storageLife,
        hardinessZone,
        waterNeeds,
        checkboxStates
    ]);

    // Helper function to safely convert array fields to searchable strings
    const arrayToString = (field) => {
        try {
            if (!field) return '';
            if (Array.isArray(field)) return field.join(' ');
            if (typeof field === 'string') return field;
            return String(field);
        } catch (error) {
            console.error('Error in arrayToString:', error, field);
            return '';
        }
    };

    // Helper function to safely check if a value is in a JSONB array field
    const isInJsonbField = (field, value) => {
        try {
            if (!field || !value) return false;

            // Handle JSONB arrays (parsed as JavaScript arrays)
            if (Array.isArray(field)) {
                return field.some(item =>
                    typeof item === 'string' ?
                        item.toLowerCase() === value.toLowerCase() :
                        item === value
                );
            }

            // Handle string fields
            if (typeof field === 'string') {
                return field.toLowerCase().includes(value.toLowerCase());
            }

            return false;
        } catch (error) {
            console.error('Error in isInJsonbField:', error, field, value);
            return false;
        }
    };

    // Helper function to check string fields with exact or partial match
    const matchesStringField = (field, value) => {
        try {
            if (!field || !value) return false;
            return field.toLowerCase() === value.toLowerCase();
        } catch (error) {
            console.error('Error in matchesStringField:', error, field, value);
            return false;
        }
    };

    // Helper function to check boolean fields safely
    const checkBooleanField = (plantField) => {
        try {
            if (typeof plantField === 'boolean') return plantField;
            if (typeof plantField === 'string') {
                const lower = plantField.toLowerCase();
                return lower === 'true' || lower === 'high' || lower === 'yes';
            }
            return false;
        } catch (error) {
            console.error('Error in checkBooleanField:', error, plantField);
            return false;
        }
    };

    const filteredPlants = useMemo(() => {
        try {
            // If no filters are active, return empty array
            if (!hasActiveFilters) return [];

            console.log('Filtering plants. Total plants:', allPlants?.length || 0);
            console.log('Active filters:', { plantType, growingSeason, lifecycle, soilPreference, sunlightLevels, hardinessZone, waterNeeds });

            // Ensure allPlants is an array
            if (!Array.isArray(allPlants)) {
                console.error('allPlants is not an array:', allPlants);
                return [];
            }

            return allPlants.filter(plant => {
                try {
                    if (!plant || typeof plant !== 'object') return false;

                    // Text search
                    if (searchTerm) {
                        const term = searchTerm.toLowerCase();
                        const searchableFields = [
                            plant.plant_name || '',
                            plant.long_description || '',
                            plant.plant_type || '',
                            plant.short_description || '',
                            arrayToString(plant.planting_season)
                        ];

                        const hasMatch = searchableFields.some(field => {
                            try {
                                return String(field).toLowerCase().includes(term);
                            } catch (error) {
                                console.error('Error in text search:', error, field);
                                return false;
                            }
                        });

                        if (!hasMatch) return false;
                    }

                    // Dropdown filters with correct field mapping
                    if (plantType && !matchesStringField(plant.plant_type, plantType)) return false;
                    if (growingSeason && !isInJsonbField(plant.planting_season, growingSeason)) return false;
                    if (lifecycle && !matchesStringField(plant.lifecycle, lifecycle)) return false;
                    if (soilPreference && !isInJsonbField(plant.soil_type, soilPreference)) return false;
                    if (sunlightLevels && !matchesStringField(plant.sunlight, sunlightLevels)) return false;
                    if (hardinessZone && !isInJsonbField(plant.hardiness_zones, hardinessZone)) return false;
                    if (waterNeeds && !matchesStringField(plant.water_needs, waterNeeds)) return false;
                    if (plantSize && !matchesStringField(plant.plant_size, plantSize)) return false;
                    if (storageLife && !matchesStringField(plant.storage_life, storageLife)) return false;

                    // Checkbox filters - mapping to correct database field names
                    if (checkboxStates?.droughtTolerant && plant.drought_tolerance !== 'High') return false;
                    if (checkboxStates?.pollinatorFriendly && !checkBooleanField(plant.pollinator_friendly)) return false;
                    if (checkboxStates?.organicPestControl && !checkBooleanField(plant.organic_pest_control)) return false;
                    if (checkboxStates?.selfPollinating && !checkBooleanField(plant.self_pollinating)) return false;
                    if (checkboxStates?.petSafeNontoxic && !checkBooleanField(plant.pet_safe_non_toxic)) return false;
                    if (checkboxStates?.containerFriendly && !checkBooleanField(plant.container_friendly)) return false;
                    if (checkboxStates?.deerResistant && !checkBooleanField(plant.deer_resistant)) return false;
                    if (checkboxStates?.groundCover && !checkBooleanField(plant.provides_ground_cover)) return false;
                    if (checkboxStates?.climbingViningPlant && !checkBooleanField(plant.is_climbing_or_vining_plant)) return false;
                    if (checkboxStates?.nitrogenFixer && !checkBooleanField(plant.nitrogen_fixer)) return false;
                    if (checkboxStates?.providesWildlifeCover && !checkBooleanField(plant.provides_wildlife_cover)) return false;
                    if (checkboxStates?.continuouslyHarvestable && !checkBooleanField(plant.continuously_harvestable)) return false;
                    if (checkboxStates?.allAtOnceHarvest && !checkBooleanField(plant.single_harvest)) return false;
                    if (checkboxStates?.goodForCanningOrPreserving && !checkBooleanField(plant.good_for_preserving)) return false;
                    if (checkboxStates?.medicinalProperties && !checkBooleanField(plant.medicinal_properties)) return false;
                    if (checkboxStates?.ornamentalFoliage && !checkBooleanField(plant.ornamental_foliage)) return false;
                    if (checkboxStates?.fragrantFlowers && !checkBooleanField(plant.fragrant_flowers)) return false;
                    if (checkboxStates?.edibleFlowers && !checkBooleanField(plant.edible_flowers)) return false;
                    if (checkboxStates?.companionPlant && !checkBooleanField(plant.good_companion_plant)) return false;

                    // String-based checkbox filters
                    if (checkboxStates?.fastGrowth && plant.growth_speed !== 'High') return false;
                    if (checkboxStates?.coldTolerant && plant.frost_tolerance !== 'High') return false;
                    if (checkboxStates?.heatTolerant && plant.heat_tolerance !== 'High') return false;

                    // Note: Some checkboxes don't have corresponding database fields yet
                    // These would need to be added to the database schema:
                    // - seedSavingFriendly
                    // - lowMaintenance

                    return true;
                } catch (error) {
                    console.error('Error filtering individual plant:', error, plant);
                    return false;
                }
            });
        } catch (error) {
            console.error('Error in filteredPlants useMemo:', error);
            return [];
        }
    }, [allPlants, searchTerm, plantType, growingSeason, lifecycle, soilPreference, sunlightLevels, plantSize, storageLife, hardinessZone, waterNeeds, checkboxStates, hasActiveFilters]);

    if (loading) {
        return (
            <div className="result-output-loading-state">
                <div className="loading-state-text">Loading plants...</div>
            </div>
        );
    }

    // Show empty state when no filters are active
    if (!hasActiveFilters) {
        return (
            <div className="result-output-start-searching-container">
                <p className="start-searching-text">Start searching to find plants</p>
                <p className="start-searching-secondary-text">Use the search bar or filters above to discover plants that match your needs</p>
            </div>
        );
    }

    // Show no results message when filters are active but no plants match
    if (hasActiveFilters && filteredPlants.length === 0) {
        return (
            <div className="result-output-no-plants-found-container">
                <p className="no-plants-found-text">No plants found</p>
                <p className="no-plants-found-secondary-text">Try adjusting your search criteria or filters</p>
            </div>
        );
    }

    // Show filtered results
    return (
        <div className="result-output-plant-card-container">
            {filteredPlants.map(plant => (
                <PlantFinderPlantResultCard
                    key={plant?.id || plant?.plant_name || Math.random()}
                    plant={plant}
                />
            ))}
        </div>
    );
}

export default PlantFinderPlantResults;